<template>
  <div class="a11y-setup" role="dialog" aria-labelledby="a11y-setup-title" aria-modal="true">
    <div class="a11y-setup__card">
      <div class="a11y-setup__header">
        <h1 id="a11y-setup-title" class="a11y-setup__title">
          One last step: enable remote control
        </h1>
        <p class="a11y-setup__lead">
          Studio can only mirror and tap this screen when Lucimark Accessibility is ON.
          Follow the steps below, then tap Continue.
        </p>
        <span
          class="a11y-setup__pill"
          :class="accessibilityOn ? 'a11y-setup__pill--on' : 'a11y-setup__pill--off'"
          :aria-label="accessibilityOn ? 'Accessibility ON' : 'Accessibility OFF'"
        >
          Accessibility {{ accessibilityOn ? 'ON' : 'OFF' }}
        </span>
      </div>

      <ol class="a11y-setup__howto" aria-label="How to enable remote control">
        <li>
          <strong>Usually start here:</strong>
          tap <em>Open Accessibility</em> (step 2). Settings unlock for this setup.
        </li>
        <li>
          In the Android list, find <strong>Lucimark</strong> (or Lucimark Player) and turn it
          <strong>ON</strong>. Confirm if Android asks.
        </li>
        <li>
          Press Back until you return to this screen. The badge above should say
          <strong>Accessibility ON</strong>.
        </li>
        <li>
          Tap <strong>Continue</strong>. If it is still OFF, wait or open Accessibility again.
        </li>
      </ol>

      <ol class="a11y-setup__steps">
        <li class="a11y-setup__step">
          <div class="a11y-setup__step-copy">
            <span class="a11y-setup__step-num">1</span>
            <div>
              <p class="a11y-setup__step-title">App info (optional)</p>
              <p class="a11y-setup__step-desc">
                Only if Android blocked Accessibility: open Lucimark app info → allow
                <em>restricted settings</em> (wording varies by device) → return here.
              </p>
            </div>
          </div>
          <button type="button" class="a11y-setup__btn a11y-setup__btn--ghost" @click="onOpenAppInfo">
            Open app info
          </button>
        </li>
        <li class="a11y-setup__step">
          <div class="a11y-setup__step-copy">
            <span class="a11y-setup__step-num">2</span>
            <div>
              <p class="a11y-setup__step-title">Accessibility (required)</p>
              <p class="a11y-setup__step-desc">
                Opens system Accessibility. Turn <strong>Lucimark</strong> ON, then come back
                to this screen.
              </p>
            </div>
          </div>
          <button type="button" class="a11y-setup__btn a11y-setup__btn--ghost" @click="onOpenAccessibility">
            Open Accessibility
          </button>
        </li>
      </ol>

      <p
        v-if="statusHint"
        class="a11y-setup__hint"
        :class="statusHintTone === 'warn' ? 'a11y-setup__hint--warn' : 'a11y-setup__hint--info'"
        role="status"
      >
        {{ statusHint }}
      </p>

      <div class="a11y-setup__actions">
        <button type="button" class="a11y-setup__btn a11y-setup__btn--ghost" @click="onSkip">
          Skip for now
        </button>
        <button
          type="button"
          class="a11y-setup__btn a11y-setup__btn--primary"
          :disabled="continuing"
          @click="onContinue"
        >
          {{ continuing ? 'Waiting…' : 'Continue' }}
        </button>
      </div>

      <p class="a11y-setup__footer">
        Playback works without remote control. Tap <strong>Skip for now</strong> to register the
        screen and enable Lucimark under Accessibility later (use the field-service PIN unlock if
        the app is locked full-screen).
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  isAccessibilityServiceEnabled,
  openAccessibilitySettings,
  openAppInfo
} from '@/helpers/androidAccessibility'
import { exitAndroidKioskLock, applyAndroidKioskLock } from '@/helpers/androidKioskLock'
import { setAndroidMaintenanceSuppressUntil } from '@/helpers/androidProcessSelfHeal'

/** How long Continue waits for Accessibility before giving up (no infinite Checking). */
const CONTINUE_WAIT_MS = 45_000
const CONTINUE_POLL_MS = 1_500
/** Same window as field-service unlock (`KIOSK_UNLOCK_WINDOW_MS`). */
const SETUP_UNLOCK_WINDOW_MS = 15 * 60 * 1000

const emit = defineEmits<{
  done: []
  skip: []
}>()

const accessibilityOn = ref(false)
const continuing = ref(false)
const statusHint = ref<string | null>(null)
const statusHintTone = ref<'info' | 'warn'>('info')
let pollTimer: ReturnType<typeof setInterval> | null = null
let continuePollTimer: ReturnType<typeof setInterval> | null = null
let continueDeadlineTimer: ReturnType<typeof setTimeout> | null = null

async function refreshStatus(): Promise<boolean> {
  const on = await isAccessibilityServiceEnabled()
  accessibilityOn.value = on
  return on
}

function clearContinuePollTimer(): void {
  if (continuePollTimer != null) {
    clearInterval(continuePollTimer)
    continuePollTimer = null
  }
  if (continueDeadlineTimer != null) {
    clearTimeout(continueDeadlineTimer)
    continueDeadlineTimer = null
  }
}

function stopContinuePoll(): void {
  clearContinuePollTimer()
  continuing.value = false
}

/**
 * Lock Task blocks Settings. First-run remote-setup is allowed to drop kiosk without the
 * field-service PIN so the operator can toggle Accessibility (see androidKioskLock.ts).
 * Callers must re-arm Lock Task when leaving this screen (done / skip / unmount).
 */
async function unlockKioskForSettings(): Promise<void> {
  await exitAndroidKioskLock()
  await setAndroidMaintenanceSuppressUntil(Date.now() + SETUP_UNLOCK_WINDOW_MS)
}

async function rearrestKioskAfterSetup(): Promise<void> {
  // Drop the setup suppress window so self-heal / Lock Task resume normally.
  await setAndroidMaintenanceSuppressUntil(0)
  await applyAndroidKioskLock()
}

onMounted(() => {
  void refreshStatus()
  pollTimer = setInterval(() => {
    void refreshStatus()
  }, 2000)
})

onBeforeUnmount(() => {
  if (pollTimer != null) {
    clearInterval(pollTimer)
    pollTimer = null
  }
  stopContinuePoll()
  void rearrestKioskAfterSetup()
})

watch(accessibilityOn, (on) => {
  if (!on) {
    return
  }
  statusHint.value = null
  if (continuing.value) {
    stopContinuePoll()
    void finishDone()
  }
})

async function finishDone(): Promise<void> {
  await rearrestKioskAfterSetup()
  emit('done')
}

async function onOpenAppInfo(): Promise<void> {
  statusHint.value = null
  await unlockKioskForSettings()
  await openAppInfo()
  statusHintTone.value = 'info'
  statusHint.value
    = 'In App info: allow restricted settings for Lucimark if shown, then return and tap Open Accessibility.'
}

async function onOpenAccessibility(): Promise<void> {
  statusHint.value = null
  await unlockKioskForSettings()
  await openAccessibilitySettings()
  statusHintTone.value = 'info'
  statusHint.value
    = 'Find Lucimark in the Accessibility list, turn it ON, press Back to this screen, then Continue.'
}

async function onSkip(): Promise<void> {
  stopContinuePoll()
  await rearrestKioskAfterSetup()
  emit('skip')
}

async function onContinue(): Promise<void> {
  if (await refreshStatus()) {
    statusHint.value = null
    await finishDone()
    return
  }
  clearContinuePollTimer()
  continuing.value = true
  statusHintTone.value = 'info'
  statusHint.value
    = 'Opening Accessibility… Turn Lucimark ON, then return. Waiting up to 45 seconds.'
  await unlockKioskForSettings()
  await openAccessibilitySettings()
  continuePollTimer = setInterval(() => {
    void refreshStatus()
  }, CONTINUE_POLL_MS)
  continueDeadlineTimer = setTimeout(() => {
    stopContinuePoll()
    statusHintTone.value = 'warn'
    statusHint.value
      = 'Still OFF. Tap Open Accessibility, enable Lucimark, come back, then Continue — or Skip for now.'
  }, CONTINUE_WAIT_MS)
}
</script>

<style scoped>
.a11y-setup {
  position: absolute;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: #0b1220;
  color: #e8eef7;
  font-family: system-ui, -apple-system, Segoe UI, sans-serif;
}

.a11y-setup__card {
  width: min(28rem, 100%);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.a11y-setup__header {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.a11y-setup__title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 650;
  line-height: 1.25;
  letter-spacing: -0.01em;
}

.a11y-setup__lead {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: rgba(232, 238, 247, 0.78);
}

.a11y-setup__howto {
  margin: 0;
  padding: 0.85rem 0.85rem 0.85rem 1.6rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  font-size: 0.8rem;
  line-height: 1.4;
  color: rgba(232, 238, 247, 0.82);
}

.a11y-setup__howto li::marker {
  font-weight: 700;
  color: #6ee7b7;
}

.a11y-setup__howto strong {
  font-weight: 650;
  color: #e8eef7;
}

.a11y-setup__howto em {
  font-style: normal;
  font-weight: 650;
  color: #bae6fd;
}

.a11y-setup__pill {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  font-size: 0.75rem;
  font-weight: 650;
  letter-spacing: 0.02em;
}

.a11y-setup__pill--off {
  background: rgba(248, 113, 113, 0.15);
  color: #fca5a5;
  border: 1px solid rgba(248, 113, 113, 0.35);
}

.a11y-setup__pill--on {
  background: rgba(52, 211, 153, 0.15);
  color: #6ee7b7;
  border: 1px solid rgba(52, 211, 153, 0.4);
}

.a11y-setup__steps {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.a11y-setup__step {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.a11y-setup__step-copy {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-width: 0;
}

.a11y-setup__step-num {
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(52, 211, 153, 0.2);
  color: #6ee7b7;
  font-size: 0.75rem;
  font-weight: 700;
}

.a11y-setup__step-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
}

.a11y-setup__step-desc {
  margin: 0.2rem 0 0;
  font-size: 0.8rem;
  line-height: 1.35;
  color: rgba(232, 238, 247, 0.7);
}

.a11y-setup__hint {
  margin: 0;
  padding: 0.65rem 0.85rem;
  border-radius: 0.55rem;
  font-size: 0.8rem;
  line-height: 1.4;
}

.a11y-setup__hint--info {
  background: rgba(56, 189, 248, 0.12);
  border: 1px solid rgba(56, 189, 248, 0.35);
  color: #bae6fd;
}

.a11y-setup__hint--warn {
  background: rgba(251, 191, 36, 0.12);
  border: 1px solid rgba(251, 191, 36, 0.4);
  color: #fde68a;
}

.a11y-setup__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: flex-end;
}

.a11y-setup__btn {
  border-radius: 0.55rem;
  padding: 0.55rem 0.95rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
}

.a11y-setup__btn:disabled {
  opacity: 0.6;
  cursor: wait;
}

.a11y-setup__btn--ghost {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.18);
  color: #e8eef7;
}

.a11y-setup__btn--primary {
  background: #34d399;
  color: #0b1220;
}

.a11y-setup__footer {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: rgba(232, 238, 247, 0.55);
  text-align: center;
}
</style>
